import {
  INJECTOR,
  Injectable,
  Signal,
  WritableSignal,
  computed,
  inject,
  linkedSignal,
  resource,
  signal,
} from '@angular/core';
import {
  SOFTLINE_FEATURE_UTILITIES_VKFORGS,
  VkforgStore,
} from '@softapps/allgemein/utils';
import { handleRequestError } from '@softline/application';
import {
  AuthenticationContextStore,
  SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
} from '@softline/auth';
import { Store } from '@softline/core';
import { FieldOkComponentService } from '@softline/dynamic';
import { MessageBarService, translator } from '@softline/ui-core';
import { firstValueFrom } from 'rxjs';
import { ApiQuery } from '../models/api-query';
import { AuthenticationContext } from '../models/authentication-context';
import { Category } from '../models/category';
import { ChartData } from '../models/chart-data';
import { Configuration } from '../models/configuration';
import { FieldOkResult } from '../models/field-ok-result';
import { ProductWithAdditionalData } from '../models/product-with-additional-data';
import { SalesOrganization } from '../models/sales-organization';
import { StorageLocation } from '../models/storage-location';
import { StorageLocationScanResult } from '../models/storage-location-scan-result';
import { Total } from '../models/total';
import { AbcAnalysisRepository } from '../repositories/abc-analysis.repository';

@Injectable()
export class AbcAnalysisService {
  //* ------ PRIVATE ------ *//
  private readonly injector = inject(INJECTOR);
  private readonly store = inject(Store);
  private readonly fieldOkService = inject(FieldOkComponentService);
  private readonly repository = inject(AbcAnalysisRepository);
  private readonly messageBarService = inject(MessageBarService);

  private readonly t = translator();

  /* RESOURCES */
  private readonly storageResource = resource({
    request: () => ({ storageId: this.storageId() }),
    loader: async ({ request }) => {
      if (!request.storageId) return undefined;
      try {
        return (await firstValueFrom(
          this.fieldOkService.loadResult(
            'lagerplatz',
            {
              filter: null,
              multiValued: false,
              parameters: {
                idvkforg: this.authenticationContext()?.vkforgid ?? 1,
              },
              maxAbfrageResults: 1000,
            },
            request.storageId
          )
        )) as FieldOkResult<StorageLocation> | undefined;
      } catch (e) {
        handleRequestError(e, this.injector, { show: true });
        return undefined;
      }
    },
  });

  private readonly categoriesResource = resource({
    request: () => ({
      storageKey: this.storageKey(),
      configuration: this.configuration(),
      storageId: this.storageId(),
      salesOrganization: this.salesOrganization(),
    }),
    loader: async ({ request }) => {
      if (!request.storageId || !request.storageKey || !request.configuration)
        return undefined;
      if (!request.salesOrganization) {
        this.messageBarService.error(
          'Keine Verkaufsorganisation im Authentifizierungskontext'
        );
        return undefined;
      }
      const query: ApiQuery = {
        storageLocation: request.storageKey,
        abcAnalysisId: request.configuration.id,
        salesOrganization: request.salesOrganization,
      };
      try {
        const categories = await this.repository.getCategories(query);
        if (categories === null) return [];
        return categories;
      } catch (error) {
        handleRequestError(error, this.injector, { show: true });
        return undefined;
      }
    },
  });

  private readonly productsResource = resource({
    request: () => ({
      categoryLetter: this.categoryLetter(),
      storageKey: this.storageKey(),
      configuration: this.configuration(),
      salesOrganization: this.salesOrganization(),
    }),
    loader: async ({ request }) => {
      if (!request.storageKey || !request.categoryLetter || !request.configuration)
        return undefined;
      if (!request.salesOrganization) {
        this.messageBarService.error(
          'Keine Verkaufsorganisation im Authentifizierungskontext'
        );
        return undefined;
      }
      const query: ApiQuery = {
        storageLocation: request.storageKey,
        abcAnalysisId: request.configuration.id,
        salesOrganization: request.salesOrganization,
      };
      const segmentId = request.categoryLetter.toString();
      try {
        const products = await this.repository.getProducts(segmentId, query);
        if (products === null) return [];
        return products;
      } catch (error) {
        handleRequestError(error, this.injector, { show: true });
        return undefined;
      }
    },
  });

  /* SIGNALS */
  private readonly storageLocation: WritableSignal<
    FieldOkResult<StorageLocation> | undefined
  > = this.storageResource.value;

  private readonly authenticationContext = this.store.signal<
    AuthenticationContext | undefined
  >(SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT, AuthenticationContextStore.getters.data);

  private readonly salesOrganizations = this.store.signal<SalesOrganization[]>(
    SOFTLINE_FEATURE_UTILITIES_VKFORGS,
    VkforgStore.getters.all
  );

  private readonly salesOrganization = computed<string | undefined>(() => {
    const authContext = this.authenticationContext();
    const orgs = this.salesOrganizations();
    if (!authContext || !authContext.vkforgid || !orgs || orgs.length === 0)
      return undefined;
    const userVkforgId = authContext.vkforgid;
    const org = orgs.find((v) => v.id === userVkforgId) as SalesOrganization;
    if (!org) return undefined;
    return org.vkforg;
  });

  //* ------ PUBLIC ------ *//
  public readonly configuration = signal<Configuration | null>(null);

  public readonly storageLocationLoading = this.storageResource.isLoading;
  public readonly storageId = signal<string | number | undefined>(undefined);

  public readonly categories: WritableSignal<Category[] | undefined> =
    this.categoriesResource.value;
  public readonly categoriesLoading = this.categoriesResource.isLoading;

  public readonly categoryLetter = signal<string | number | undefined>(undefined);

  public readonly products: WritableSignal<ProductWithAdditionalData[] | undefined> =
    this.productsResource.value;
  public readonly productsLoading = this.productsResource.isLoading;

  /* HELPER */
  public readonly storageLocationData = computed<StorageLocation | null>(() => {
    const storageLocation = this.storageLocation();
    if (!storageLocation) return null;
    return storageLocation.data as StorageLocation | null;
  });

  public readonly chartDataArray: Signal<ChartData[][]> = computed(() => {
    const categories = this.categories();
    if (!categories) return [];
    return [
      categories.map((c) => ({
        value: c.quantity,
        color: c.segment.segmentColor,
        label: 'Segment ' + c.segment.segment,

        title: this.t('#ABC_ANALYSIS.SERVICE.QUANTITY')(),
        numberFormat: '1.0-0',
        totalSuffix: this.t('#ABC_ANALYSIS.SERVICE.PRODUCT')(),
      })),
      categories.map((c) => ({
        value: c.margin,
        color: c.segment.segmentColor,
        label: 'Segment ' + c.segment.segment,

        title: this.t('#ABC_ANALYSIS.SERVICE.CONTRIBUTION_MARGIN')(),
        numberFormat: '1.2-2',
        totalSuffix: 'EUR (€)',
      })),
      categories.map((c) => ({
        value: c.net,
        color: c.segment.segmentColor,
        label: 'Segment ' + c.segment.segment,

        title: this.t('#ABC_ANALYSIS.SERVICE.NET')(),
        numberFormat: '1.2-2',
        totalSuffix: 'EUR (€)',
      })),
      categories.map((c) => ({
        value: c.stockValue,
        color: c.segment.segmentColor,
        label: 'Segment ' + c.segment.segment,

        title: this.t('#ABC_ANALYSIS.SERVICE.STOCK_VALUE')(),
        numberFormat: '1.2-2',
        totalSuffix: 'EUR (€)',
      })),
    ];
  });

  public readonly categoriesTotal = computed<Total>(() => {
    const categories = this.categories();
    return {
      quantity: categories?.reduce((total, c) => total + c.quantity, 0) ?? 0,
      margin: categories?.reduce((total, c) => total + c.margin, 0) ?? 0,
      net: categories?.reduce((total, c) => total + c.net, 0) ?? 0,
      stockValue: categories?.reduce((total, c) => total + c.stockValue, 0) ?? 0,
    };
  });

  public readonly category = computed<Category | null>(() => {
    const categories = this.categories();
    if (!categories) return null;
    return categories.find((c) => c.segment.segment === this.categoryLetter()) ?? null;
  });

  public readonly storageKey = linkedSignal<string | null>(() => {
    return this.storageLocationData()?.lagerplatz ?? null;
  });

  /* LOCAL STORAGE */
  public loadConfiguration(locationKey: string): Configuration | null {
    const config = this.repository.loadConfiguration(locationKey);
    if (!config) return null;
    if (!this.configuration() || this.configuration()?.id !== config.id)
      this.configuration.set(config);
    return config;
  }

  public saveConfiguration(locationKey: string, config: Configuration): void {
    this.repository.saveConfiguration(locationKey, config);
    this.configuration.set(config);
    return;
  }

  /* API REQUESTS */
  public async scanStorageLocation(
    storageLocationCode: string
  ): Promise<StorageLocationScanResult> {
    return await this.repository.scanStorageLocation(storageLocationCode);
  }

  public async getCategories(
    storageLocation: StorageLocation | StorageLocationScanResult
  ): Promise<void> {
    this.storageId.set(storageLocation.id);
    this.storageKey.set(storageLocation.lagerplatz);
  }

  public async getConfigurations(): Promise<Configuration[]> {
    const query = {
      filter: '',
      multiValued: false,
      parameters: {},
    };
    const response = await this.repository.getConfigurations(query);
    const data = response?.data;
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object')
      return Object.keys(data)
        .map((key) => data[key])
        .filter((item) => item != null) as Configuration[];
    return [];
  }
}
